/**
 * Import React libraries.
 */
import * as React from 'react';

/**
 * Import custom components.
 */
import styled from 'styled-components';

/**
 * Import custom components.
 */
import { UIIconButton, Button } from '../Button';
import * as icons from '../icons/index';

/**
 * Import custom styles.
 */
import { DropdownContent, DropdownButton as DB } from './CommonStyling';

/**
 * Import custom types.
 */
import { DropdownButtonProps } from './DropdownButtonTypes';

const DropdownButton = ({ list, icon, iconSize, text, buttonSize, variant, isIcon, disabled, width, iconColor, onClick }: DropdownButtonProps) => {
  // Globally used variables within the view.
  const [dropdownOpen, setDropdownOpen] = React.useState<boolean>(false);
  const dropdownContentRef = React.useRef<HTMLDivElement>(null);

  /**
   * Adds custom event handler.
   */
  React.useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  });

  /**
   * Checks if we have clicked outside the dropdown - then close the dropdown.
   * @param event - Event handler from the mouse click.
   */
  const handleClickOutside = (event: any) => {
    if (dropdownContentRef?.current && !dropdownContentRef.current.contains(event.target)) {
      if (dropdownOpen) {
        setDropdownOpen(false);
      }
    }
  };

  /**
   * Does all required actions when list item was selected.
   * @param {Object} event - Event handler from the mouse click.
   * @param {string} id - ID of the clicked list item.
   */
  const clickListItem = (event: React.MouseEvent, id: string): void => {
    // Prevent default actions
    event.preventDefault();

    // Do all required actions
    onClick(id);
    setDropdownOpen(false);
  };

  /**
   * Retrieves elements for the dropdown list.
   * @returns Dropdown list elements HTML code.
   */
  const getElements = () => {
    var options = list;

    if (options.length === 0) {
      return (
        <DB disabled>
          <span>There are no options available</span>
        </DB>
      );
    }
    return options.map(item => {
      return (
        <DB
          type="button"
          onClick={(event: React.MouseEvent) => clickListItem(event, item.id)}
          key={item.id}>
          <span>{item.label}</span>
        </DB>
      );
    });
  };

  /**
   * Renders Icon Button dropdown menu.
   * @returns HTML View for the Icon button dropdown menu.
   */
  const renderIconButton = () => {
    return (
      <UIIconButton action={() => setDropdownOpen(!dropdownOpen)} disabled={disabled}>
        { icon && icons[icon]({ size: iconSize, color: iconColor }) }
      </UIIconButton>
    );
  };

  /**
   * Renders button dropdown menu.
   * @returns HTML view for the button dropdown menu.
   */
  const renderButton = () => {
    return (
      <Button variant={variant} size={buttonSize} onClick={() => setDropdownOpen(!dropdownOpen)} disabled={disabled}>
        { text }
      </Button>
    )
  }

  return (
    <>
    { isIcon
        ? renderIconButton()
        : renderButton()
    }

    {!disabled && (
      <DropdownContent isButton={false} style={{width}} ref={dropdownContentRef} className={dropdownOpen ? 'show' : ''}>
        {getElements()}
      </DropdownContent>
    )}
    </>
  );
};

export default DropdownButton;
